import React, { useState, useEffect } from "react";
import useAxios from "../../utils/useAxios";
import { useParams, Link, useNavigate } from "react-router-dom";

const PartnerApplicationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const axiosAPI = useAxios();

  useEffect(() => {
    const fetchApplicationDetails = async () => {
      try {
        const response = await axiosAPI.get(`/api/partner-applications/${id}`);
        if (response.data.success) {
          setApplication(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching application details:", error);
      }
      setLoading(false);
    };
    fetchApplicationDetails();
  }, [id]);

  const updateStatus = async (newStatus) => {
    if (!window.confirm(`Are you sure you want to ${newStatus} this application?`)) return;

    try {
      const response = await axiosAPI.put(`/api/partner-applications/${id}/status`, { status: newStatus });
      if (response.data.success) {
        alert(response.data.message);
        navigate("/user/partner-applications");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Failed to update status");
    }
  };

  if (loading) return <div className="content"><p>Loading...</p></div>;
  if (!application) return <div className="content"><p>Application not found.</p></div>;

  const renderFileLink = (url, label) => {
    if (!url) return <span className="text-muted">Not Uploaded</span>;
    // For local files we need to point to the backend URL, but assuming proxy or absolute URL:
    // If url starts with /uploads, we prefix it with the backend host if needed. 
    // Here we can just use the url directly if it's served by the backend proxy.
    const fileUrl = url.startsWith('http') ? url : `http://localhost:5001${url}`;
    return (
      <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
        <i className="fa-solid fa-file"></i> View {label}
      </a>
    );
  };

  const menuPhotos = application.menu_photos ? JSON.parse(application.menu_photos) : [];
  const topDishes = application.top_dishes ? JSON.parse(application.top_dishes) : [];

  return (
    <div className="content">
      <div className="page-header">
        <div className="page-title">
          <h4>Review Partner Application</h4>
          <h6>Application Details for {application.restaurant_name}</h6>
        </div>
        <div className="page-btn d-flex" style={{ gap: '10px' }}>
          <Link to="/user/partner-applications" className="btn btn-secondary">Back to List</Link>
          {application.status === 'pending' && (
            <>
              <button onClick={() => updateStatus('approved')} className="btn btn-success">Approve</button>
              <button onClick={() => updateStatus('rejected')} className="btn btn-danger">Reject</button>
            </>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-lg-6 col-sm-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">1. Basic Info</h5>
            </div>
            <div className="card-body">
              <p><strong>Restaurant Name:</strong> {application.restaurant_name}</p>
              <p><strong>City:</strong> {application.restaurant_city}</p>
              <p><strong>Mobile No:</strong> {application.mobile_no}</p>
              <p><strong>Owner Name:</strong> {application.owner_name}</p>
              <p><strong>Owner Email:</strong> {application.owner_email}</p>
              <p><strong>Status:</strong> <span className={`badges ${application.status === 'approved' ? 'bg-lightgreen' : application.status === 'rejected' ? 'bg-lightred' : 'bg-lightyellow'}`}>{application.status.toUpperCase()}</span></p>
            </div>
          </div>
        </div>

        <div className="col-lg-6 col-sm-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">2. Legal Documents</h5>
            </div>
            <div className="card-body">
              <p><strong>FSSAI No:</strong> {application.fssai_no} (Exp: {application.fssai_expiry})</p>
              <div className="mb-3">{renderFileLink(application.fssai_doc_url, "FSSAI Document")}</div>
              
              <p><strong>PAN No:</strong> {application.pan_no} ({application.pan_name})</p>
              <div className="mb-3">{renderFileLink(application.pan_doc_url, "PAN Card")}</div>
              
              <p><strong>GSTIN:</strong> {application.gst_no || "N/A"}</p>
              <div className="mb-3">{renderFileLink(application.gst_doc_url, "GST Certificate")}</div>
            </div>
          </div>
        </div>

        <div className="col-lg-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">3. Outlet & Menu Photos</h5>
            </div>
            <div className="card-body row">
              <div className="col-md-4">
                <h6>Storefront</h6>
                {renderFileLink(application.storefront_photo, "Storefront Photo")}
              </div>
              <div className="col-md-4">
                <h6>Kitchen</h6>
                {renderFileLink(application.kitchen_photo, "Kitchen Photo")}
              </div>
              <div className="col-md-4">
                <h6>Top Dish Photo</h6>
                {renderFileLink(application.top_dish_photo, "Top Dish Photo")}
              </div>
            </div>
            <hr />
            <div className="card-body">
              <h6>Menu Photos ({menuPhotos.length})</h6>
              <div className="d-flex" style={{ gap: '10px', flexWrap: 'wrap' }}>
                {menuPhotos.map((url, i) => (
                  <div key={i}>{renderFileLink(url, `Menu Page ${i+1}`)}</div>
                ))}
              </div>
              
              <h6 className="mt-4">Top Dishes List</h6>
              <ul>
                {topDishes.map((dish, i) => (
                  <li key={i}>{dish}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="col-lg-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">4. Bank Account Details</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <p><strong>Bank Name:</strong> {application.bank_name}</p>
                  <p><strong>Account Holder:</strong> {application.account_holder_name}</p>
                  <p><strong>Account No:</strong> {application.bank_account_no}</p>
                  <p><strong>IFSC Code:</strong> {application.ifsc_code}</p>
                </div>
                <div className="col-md-6">
                  <h6>Cancelled Cheque / Passbook</h6>
                  {renderFileLink(application.cheque_doc_url, "Bank Document")}
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default PartnerApplicationDetail;
